import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, DollarSign, ArrowUpRight, Clock } from 'lucide-react';

function FinancialsSection({ content }) {
  const projections = [
    { year: 'Year 1', revenue: '$600K', users: '10,000', mrr: '$80K' },
    { year: 'Year 2', revenue: '$2.5M', users: '35,000', mrr: '$250K' },
    { year: 'Year 3', revenue: '$8M', users: '100,000', mrr: '$700K' },
  ];

  const unitEconomics = [
    { metric: 'LTV', value: '$480', color: 'kore' },
    { metric: 'CAC', value: '$12', color: 'mint' },
    { metric: 'LTV:CAC', value: '40:1', color: 'gold' },
    { metric: 'Payback', value: '2 mo', color: 'lavender' },
  ];

  const costs = [
    { item: 'GCP Cloud Run', range: '$3-6K' },
    { item: 'Database (PostgreSQL)', range: '$2-4K' },
    { item: 'Redis Cache', range: '$1-2K' },
    { item: 'Storage & CDN', range: '$2-4K' },
    { item: 'Third-party Services', range: '$5-10K' },
  ];

  return (
    <section className="min-h-[calc(100vh-5rem)] flex items-center justify-center px-6 py-20">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-accent-mint/10 border border-accent-mint/20 rounded-full mb-6">
            <TrendingUp className="w-4 h-4 text-accent-mint" />
            <span className="text-sm text-accent-mint">Financial Projections</span>
          </div>
          <h2 className="section-title mb-4">
            3-Year <span className="text-gradient">Forecast</span>
          </h2>
        </motion.div>

        {/* Revenue Projections */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="grid md:grid-cols-3 gap-6 mb-8"
        >
          {projections.map((proj, index) => (
            <motion.div
              key={proj.year}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 + index * 0.1 }}
              className={`card text-center hover-lift ${index === 2 ? 'border-accent-mint/30 bg-accent-mint/5' : ''}`}
            >
              <div className="text-sm text-zinc-500 mb-2">{proj.year}</div>
              <div className="text-4xl md:text-5xl font-bold text-gradient mb-4">{proj.revenue}</div>
              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-surface-border">
                <div>
                  <div className="text-xl font-semibold text-white">{proj.users}</div>
                  <div className="text-xs text-zinc-500">Users</div>
                </div>
                <div>
                  <div className="text-xl font-semibold text-kore-400">{proj.mrr}</div>
                  <div className="text-xs text-zinc-500">MRR</div>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Unit Economics */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5 }}
            className="card"
          >
            <h3 className="text-lg font-semibold mb-6 flex items-center gap-2">
              <ArrowUpRight className="w-5 h-5 text-accent-mint" />
              Unit Economics
            </h3>
            <div className="grid grid-cols-2 gap-4">
              {unitEconomics.map((item, index) => {
                const colorClasses = {
                  kore: 'text-kore-400',
                  mint: 'text-accent-mint',
                  gold: 'text-accent-gold',
                  lavender: 'text-accent-lavender'
                };
                return (
                  <motion.div
                    key={item.metric}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.6 + index * 0.1 }}
                    className="bg-surface-elevated rounded-xl p-4 text-center"
                  >
                    <div className="text-sm text-zinc-500 mb-1">{item.metric}</div>
                    <div className={`text-2xl font-bold ${colorClasses[item.color]}`}>
                      {item.value}
                    </div>
                  </motion.div>
                );
              })}
            </div>
            <p className="text-xs text-zinc-500 mt-4 text-center">
              Exceptional unit economics driven by viral KoreCode growth
            </p>
          </motion.div>

          {/* Infrastructure Costs */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.6 }}
            className="card"
          >
            <h3 className="text-lg font-semibold mb-6 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-accent-gold" />
              Year 1 Infrastructure
            </h3>
            <div className="space-y-3">
              {costs.map((cost, index) => (
                <motion.div
                  key={cost.item}
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.7 + index * 0.05 }}
                  className="flex items-center justify-between py-2 border-b border-surface-border/50 last:border-0"
                >
                  <span className="text-sm text-zinc-400">{cost.item}</span>
                  <span className="text-sm font-medium text-white">{cost.range}</span>
                </motion.div>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t border-surface-border flex items-center justify-between">
              <span className="text-sm font-medium text-zinc-300">Total Year 1</span>
              <span className="text-lg font-bold text-accent-gold">~$27K</span>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

export default FinancialsSection;
